import { Card, notification, Typography } from "antd";
import dayjs from 'dayjs';
import { useContext, useState } from "react";
import FinancialsSection from "../components/CompanyFinancials/FinancialsSection";
import FilteredFinancialsSection from "../components/CompanyFinancials/FilteredFinancialsSection";
import FinancialsFilters from "../components/CompanyFinancials/FinancialsFilters";
import FinancialsExportButton from "../components/Dashboard/FinancialsExportButton";
import { DashboardFilters, dashboardRangeEnd, hasDashboardFilters, noDashboardFilters } from "../components/Dashboard/dashboardModel";
import DashboardHeroDetails, { DateRange } from "../components/Dashboard/DashboardHeroDetails";
import SalesLeadsGraph from "../components/Dashboard/SalesLeadsGraph";
import SoldJobRevenue from "../components/Dashboard/SoldJobRevenue";
import EmployeeUtilizationSection from "../components/Dashboard/EmployeeUtilizationSection";
import WonLostGraph from "../components/Dashboard/WonLostGraph";
import SuccessChecklist from "../components/SuccessChecklist/SuccessChecklist";
import HeroContainer from "../components/common/HeroContainer";
import EstimatesReport from "../components/Datacenter/EstimatesReport/EstimatesReport";
import useDashboardReporting from "../hooks/useDashboardReporting";
import { useFeaturesQuery } from "../domain/features/featuresQueries";
import { AuthenticationContext } from "../AuthenticationContext";
import { useAccount } from "../components/Account/accountQueries";

type NotificationType = 'success' | 'info' | 'warning' | 'error';

export type Props = {
  imgSrc: string
  tenantName: string
}

const DashboardPage = ({ imgSrc, tenantName }: Props) => {
  const [notifier, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (type: NotificationType, message: string, description: string) => {
    notifier[type]({
      message: message,
      description: description
    });
  };

  const now = dayjs()
  const defaultStarting = now.subtract(1, 'month').format('MM/DD/YYYY')
  const defaultEnding = now.format('MM/DD/YYYY')
  const dateRange: DateRange = JSON.parse((localStorage as any)?.getItem("dashboard")) ?? { starting: defaultStarting.toString(), ending: defaultEnding.toString() }

  if(!dayjs(dateRange.starting).isValid()){
    dateRange.starting = defaultStarting
  } 
  
  if(!dayjs(dateRange.ending).isValid()) {
    dateRange.ending = defaultEnding
  }

  const [startingDate, setStartingDate] = useState<string>(dateRange.starting)
  const [endingDate, setEndingDate] = useState<string>(dateRange.ending)
  // Not persisted: a shared persisted filter is what leaked the dashboard date range into Estimates (SYN-4029).
  const [filters, setFilters] = useState<DashboardFilters>(noDashboardFilters)
  const dashboardReporting = useDashboardReporting()
  const { currentUser } = useContext(AuthenticationContext)
  const features = useFeaturesQuery()
  // Reads the cached account request without fetching it again, to know when it has failed.
  const account = useAccount(false)
  // The flag reads as off until both the features and the user load; a failed features or account request means off.
  const flagResolved = features.isError || (features.isSuccess && (!!currentUser || account.isError))
  // Wait for the flag: mounting the Estimates card even briefly writes the dashboard dates into the Estimates report's saved filters.
  const showEstimatesCard = flagResolved && !dashboardReporting
  // With dashboard-reporting the reports include the whole end day; without it they keep main's cut-off.
  const queryEnd = dashboardReporting ? dashboardRangeEnd(endingDate) : endingDate

  const onSuccessChecklistSectionChange = (data: any) => {
    window.posthog.capture('success-checklist', data.eventData)
  }

  const refreshData = (data: any) => {
    if (data === null) {
      openNotificationWithIcon('warning', 'Date range not set', 'Please select a date range.')
    }
    else {
      localStorage.setItem("dashboard", JSON.stringify({ starting: data[0].format('MM/DD/YYYY'), ending: data[1].format('MM/DD/YYYY') }));
      setStartingDate(data[0].format('MM/DD/YYYY'))
      setEndingDate(data[1].format('MM/DD/YYYY'))
    }
  }

  return <div id="main" className={"main-dashboard fluid-container"}>
    <HeroContainer imgSrc={imgSrc} />
    <DashboardHeroDetails tenantName={tenantName} onRefreshData={refreshData} dateRange={dateRange} showPresets={dashboardReporting} />
    <SuccessChecklist on-section-change={onSuccessChecklistSectionChange} />
    {dashboardReporting && <Card size="small" title="Filter reports" style={{ margin: '0 0 25px' }}>
      <FinancialsFilters onChange={setFilters} />
    </Card>}
    {/* Wait for the flag so the charts query once, with the right end date, instead of refetching. */}
    {flagResolved && <section style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
      <SalesLeadsGraph start={startingDate} end={endingDate} queryEnd={queryEnd} filtersApplied={hasDashboardFilters(filters)} />
      <WonLostGraph start={startingDate} end={endingDate} queryEnd={queryEnd} filters={dashboardReporting ? filters : undefined} />
      <SoldJobRevenue start={startingDate} end={endingDate} queryEnd={queryEnd} filters={dashboardReporting ? filters : undefined} />
    </section>}
    <section>
      {showEstimatesCard && <Card
        title="Estimates"
        extra={<Typography.Link href="/#!/estimates">View Estimates</Typography.Link>}
      >
        <EstimatesReport
          summaryMode={true}
          starting={startingDate}
          ending={endingDate}
        />
      </Card>}
      {flagResolved && (dashboardReporting
        ? <Card title="Company Wide Job Costing" style={{ marginTop: '25px' }} extra={<FinancialsExportButton starting={startingDate} ending={queryEnd} filters={filters} />}>
            <FilteredFinancialsSection starting={startingDate} ending={queryEnd} filters={filters} />
          </Card>
        : <Card title="Company Wide Job Costing" style={{ marginTop: '25px' }}>
            <FinancialsSection starting={startingDate} ending={endingDate} />
          </Card>)}
      {dashboardReporting && <EmployeeUtilizationSection start={startingDate} end={endingDate} filters={filters} />}
    </section>
    {contextHolder}
  </div>
}

export default DashboardPage
